import React, { useEffect, useState } from 'react';
import { Chatbot } from './components/Chatbot';
import { DarkModeToggle } from './components/DarkModeToggle';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { IndustriesPage } from './pages/IndustriesPage';
import { WhyUsPage } from './pages/WhyUsPage';
import { ContactPage } from './pages/ContactPage';
import { Menu, X } from 'lucide-react';
export function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const sections = ['home', 'services', 'industries', 'why-us', 'contact'];
      const scrollPosition = window.scrollY + 200;
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const {
            offsetTop,
            offsetHeight
          } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };
  const navLinks = [{
    id: 'home',
    label: 'Home'
  }, {
    id: 'services',
    label: 'Services'
  }, {
    id: 'industries',
    label: 'Industries'
  }, {
    id: 'why-us',
    label: 'Why Us'
  }, {
    id: 'contact',
    label: 'Contact'
  }];
  return <div className={`min-h-screen font-sans transition-colors duration-300 ${isDarkMode ? 'dark bg-gray-900' : 'bg-white'}`} data-id="element-0">
      {/* Navigation */}
      <nav className={`
          fixed top-0 left-0 right-0 z-40 transition-all duration-300 border-b
          ${scrolled ? isDarkMode ? 'bg-gray-900/80 backdrop-blur-md border-gray-700 shadow-sm py-3' : 'bg-white/80 backdrop-blur-md border-gray-200 shadow-sm py-3' : isDarkMode ? 'bg-transparent border-transparent py-5' : 'bg-transparent border-transparent py-5'}
        `} data-id="element-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between" data-id="element-2">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')} data-id="element-3">
            <img src={isDarkMode ? 'https://cdn.magicpatterns.com/uploads/oMRWXis4tnbfKBnVV3mybr/Logo_dark.png' : 'https://cdn.magicpatterns.com/uploads/ryqYpp7zv1JktSnwG7DhGU/Logo_white.png'} alt="Akios Logo" className="h-10 w-auto" data-id="element-4" />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-2" data-id="element-5">
            {navLinks.map(link => <button key={link.id} onClick={() => scrollToSection(link.id)} className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${activeSection === link.id ? isDarkMode ? 'bg-brand-600 text-white shadow-md' : 'bg-gray-900 text-white shadow-md' : isDarkMode ? 'text-gray-300 hover:bg-gray-800 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                `} data-id="element-6">
                {link.label}
              </button>)}
            <DarkModeToggle isDark={isDarkMode} onToggle={() => setIsDarkMode(!isDarkMode)} data-id="element-7" />
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center gap-2 md:hidden" data-id="element-8">
            <DarkModeToggle isDark={isDarkMode} onToggle={() => setIsDarkMode(!isDarkMode)} data-id="element-9" />
            <button className={`p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-10">
              {isMobileMenuOpen ? <X data-id="element-11" /> : <Menu data-id="element-12" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className={`fixed inset-0 z-30 pt-24 px-6 md:hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} data-id="element-13">
          <div className="flex flex-col gap-4" data-id="element-14">
            {navLinks.map(link => <button key={link.id} onClick={() => scrollToSection(link.id)} className={`
                  p-4 text-left text-lg font-medium rounded-xl border
                  ${activeSection === link.id ? isDarkMode ? 'bg-brand-900 border-brand-700 text-brand-300' : 'bg-brand-50 border-brand-100 text-brand-700' : isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-100 text-gray-600'}
                `} data-id="element-15">
                {link.label}
              </button>)}
          </div>
        </div>}

      {/* Main Content */}
      <main className="min-h-screen" data-id="element-16">
        <section id="home" data-id="element-17">
          <HomePage onNavigate={scrollToSection} isDark={isDarkMode} data-id="element-18" />
        </section>
        <section id="services" data-id="element-19">
          <ServicesPage isDark={isDarkMode} data-id="element-20" />
        </section>
        <section id="industries" data-id="element-21">
          <IndustriesPage isDark={isDarkMode} data-id="element-22" />
        </section>
        <section id="why-us" data-id="element-23">
          <WhyUsPage onNavigate={scrollToSection} isDark={isDarkMode} data-id="element-24" />
        </section>
        <section id="contact" data-id="element-25">
          <ContactPage isDark={isDarkMode} data-id="element-26" />
        </section>
      </main>

      {/* Footer */}
      <footer className={`border-t py-12 px-6 lg:px-8 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`} data-id="element-27">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-sm" data-id="element-28">
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-29">
            © {new Date().getFullYear()} Akios. All rights reserved.
          </p>
          <div className="flex gap-6" data-id="element-30">
            <a href="#" className={`transition-colors ${isDarkMode ? 'text-gray-400 hover:text-brand-400' : 'text-gray-500 hover:text-brand-600'}`} data-id="element-31">
              Privacy
            </a>
            <a href="#" className={`transition-colors ${isDarkMode ? 'text-gray-400 hover:text-brand-400' : 'text-gray-500 hover:text-brand-600'}`} data-id="element-32">
              Terms
            </a>
            <a href="#" className={`transition-colors ${isDarkMode ? 'text-gray-400 hover:text-brand-400' : 'text-gray-500 hover:text-brand-600'}`} data-id="element-33">
              Security
            </a>
          </div>
        </div>
      </footer>

      {/* Chatbot */}
      <Chatbot isDark={isDarkMode} data-id="element-34" />
    </div>;
}